import { useCallback, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ROUTES } from "constants/routes";

import { useListOfPolicy } from "hooks/password-policy";

const useResetPassword = () => {
  const [password, setPassword] = useState<string>("");
  const [confirmedPassword, setConfirmedPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [validFieldErrors, setValidFieldErrors] = useState<string[]>([]);
  const [errorText, setErrorText] = useState<string>("");
  const [extraErrorText, setExtraErrorText] = useState<string>("");
  const [infoModalIsOpen, setInfoModalIsOpen] = useState<boolean>(false);

  const {
    listOfPolicy,
    isMinimum,
    isNumeric,
    isSpecial,
    isLowerCase,
    isUpperCase,
    isPasswordsMatch,
  } = useListOfPolicy({ password, confirmedPassword });

  const navigate = useNavigate();

  const onInfoModalOpen = useCallback(() => setInfoModalIsOpen(true), []);

  const onInfoModalClose = useCallback(() => setInfoModalIsOpen(false), []);

  const handleRedirectToLogIn = useCallback(() => {
    onInfoModalClose();
    navigate(ROUTES.signIn);
  }, [navigate, onInfoModalClose]);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
    setValidFieldErrors((prev) =>
      prev.filter((error: string) => error !== "password")
    );
    setErrorText("");
  }, []);

  const handleConfirmedPasswordChange = useCallback((value: string) => {
    setConfirmedPassword(value);
    setValidFieldErrors((prev) =>
      prev.filter((error: string) => error !== "confirmedPassword")
    );
    setExtraErrorText("");
  }, []);

  const checkFieldValidation = useCallback(
    (value: string, key: string) => () => {
      if (key === "password") {
        if (
          !value ||
          !isMinimum ||
          !isLowerCase ||
          !isUpperCase ||
          !isNumeric ||
          !isSpecial
        ) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        }

        if (!value) {
          setErrorText("Please enter the new password");
        } else if (!isMinimum) {
          setErrorText("The password must consist of at least 8 characters");
        } else if (!isLowerCase) {
          setErrorText("The password must contain one lowercase character");
        } else if (!isNumeric) {
          setErrorText("The password must contain one number");
        } else if (!isSpecial) {
          setErrorText("The password must contain one special character");
        } else if (!isUpperCase) {
          setErrorText("The password must contain one uppercase character");
        }
      }

      if (key === "confirmedPassword") {
        if (!value || !isPasswordsMatch) {
          setValidFieldErrors((prevErrors) =>
            prevErrors.includes(key) ? prevErrors : [...prevErrors, key]
          );
        }

        if (
          !!(password && confirmedPassword && password !== confirmedPassword)
        ) {
          setExtraErrorText("Passwords mismatch");
        }
      }
    },
    [
      confirmedPassword,
      isLowerCase,
      isMinimum,
      isNumeric,
      isPasswordsMatch,
      isSpecial,
      isUpperCase,
      password,
    ]
  );

  const checkFieldsValidation = useCallback(
    (values: { [key: string]: string }) =>
      Object.entries(values).forEach(([key, value]) => {
        checkFieldValidation(value, key)();
      }),
    [checkFieldValidation]
  );

  const isPasswordValid = useMemo(
    () =>
      !!(
        isLowerCase &&
        isMinimum &&
        isNumeric &&
        isSpecial &&
        isUpperCase &&
        isPasswordsMatch
      ),
    [
      isLowerCase,
      isMinimum,
      isNumeric,
      isPasswordsMatch,
      isSpecial,
      isUpperCase,
    ]
  );

  const formData = useMemo(() => {
    const values = {
      password,
      confirmedPassword,
    };

    return {
      values,
      isValid: Object.values(values).every((el) => el) && isPasswordValid,
    };
  }, [confirmedPassword, isPasswordValid, password]);

  const handleFormClear = useCallback(() => {
    setPassword("");
    setConfirmedPassword("");
  }, []);

  const handleSubmit = useCallback(() => {
    //FIXME: Add the real request to the BE
    if (formData.isValid) {
      setLoading(true);

      setTimeout(() => {
        setLoading(false);
        handleFormClear();
        onInfoModalOpen();
        console.log("Submit");
      }, 3000);
    } else {
      checkFieldsValidation(formData.values);
    }
  }, [
    checkFieldsValidation,
    formData.isValid,
    formData.values,
    handleFormClear,
    onInfoModalOpen,
  ]);

  return {
    password,
    confirmedPassword,
    loading,
    validFieldErrors,
    errorText,
    extraErrorText,
    infoModalIsOpen,
    listOfPolicy,
    handlePasswordChange,
    handleConfirmedPasswordChange,
    handleSubmit,
    checkFieldValidation,
    onInfoModalClose,
    handleRedirectToLogIn,
  };
};

export default useResetPassword;
